import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clock,
  Mail,
  User,
  Phone,
  Loader2,
  CheckCircle,
  Rocket,
  Users,
  AlertCircle
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BetaWaitlistForm = ({ role = "stylist", onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [position, setPosition] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: role,
    message: "",
    referral_source: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/beta/waitlist`, formData);
      setSubmitted(true);
      setPosition(response.data.position);
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join waitlist");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 border-green-500/30">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
            WAITLIST CONFIRMED
          </Badge>
          <h3 className="text-2xl font-bold text-white mb-2">You&apos;re on the List!</h3>
          {position && (
            <p className="text-4xl font-bold text-amber-400 mb-4">#{position}</p>
          )}
          <p className="text-stone-400 mb-6">
            We&apos;ll email you at <span className="text-white">{formData.email}</span> as 
            soon as a spot opens up. Thank you for your interest in MyHairChair!
          </p>
          <div className="bg-stone-800/50 p-4 rounded-lg">
            <p className="text-stone-300 text-sm">
              💡 <strong>Pro tip:</strong> Follow us on social media for beta updates and 
              sneak peeks of upcoming features!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 border-amber-500/30">
      <CardContent className="p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Rocket className="w-8 h-8 text-amber-400" />
          </div>
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-3">
            BETA TESTING
          </Badge>
          <h2 className="text-2xl font-bold text-white mb-2">Join Our Waitlist</h2>
          <p className="text-stone-400">
            We&apos;re currently in beta testing to bring you the best experience possible. 
            Join our waitlist to be notified when spots open up!
          </p>
        </div>

        {/* Beta Info */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-300 font-medium">Why Beta?</p>
              <p className="text-blue-200/70 text-sm">
                We&apos;re perfecting our platform with a small group of early adopters. 
                Your patience helps us deliver a polished experience for everyone.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-stone-300">Full Name *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your name"
                  className="pl-10 bg-stone-800 border-stone-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <Label className="text-stone-300">Email *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="you@example.com"
                  className="pl-10 bg-stone-800 border-stone-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <Label className="text-stone-300">Phone (optional)</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  className="pl-10 bg-stone-800 border-stone-700 text-white"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-stone-300">I want to join as...</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({...formData, role: value})}
            >
              <SelectTrigger className="bg-stone-800 border-stone-700 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stylist">Stylist / Barber</SelectItem>
                <SelectItem value="owner">Salon Owner</SelectItem>
                <SelectItem value="soldier">Marketing Partner (Soldier)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-stone-300">How did you hear about us? (optional)</Label>
            <Select
              value={formData.referral_source}
              onValueChange={(value) => setFormData({...formData, referral_source: value})}
            >
              <SelectTrigger className="bg-stone-800 border-stone-700 text-white mt-1">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="friend">Friend / Colleague</SelectItem>
                <SelectItem value="google">Google Search</SelectItem>
                <SelectItem value="referral">Referral Code</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-stone-300">Anything else you&apos;d like us to know? (optional)</Label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Tell us about yourself or your salon..."
              className="bg-stone-800 border-stone-700 text-white mt-1"
              rows={3}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold h-12"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining...</>
            ) : (
              <><Clock className="w-4 h-4 mr-2" /> Join Waitlist</>
            )}
          </Button>
        </form>

        <p className="text-stone-500 text-xs text-center mt-4">
          We&apos;ll only contact you about your waitlist status. No spam, ever.
        </p>
      </CardContent>
    </Card>
  );
};

export default BetaWaitlistForm;
